import { useState, useEffect, useCallback } from "react";
import * as clinicalDataService from "../services/clinicalDataService";

/**
 * Hook orquestador que carga los 6 datasets de una location en paralelo.
 * Cada panel tiene su propio loading state para skeleton loaders independientes.
 *
 * @param {string|null} locationKey - Key de la location seleccionada
 * @returns {{ summary, alerts, patients, careGaps, coding, recall, loading, error, refresh }}
 */
export default function useClinicalDashboard(locationKey) {
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [patients, setPatients] = useState(null);
  const [careGaps, setCareGaps] = useState(null);
  const [coding, setCoding] = useState(null);
  const [recall, setRecall] = useState(null);

  const [loading, setLoading] = useState({
    summary: true,
    alerts: true,
    patients: true,
    careGaps: true,
    coding: true,
    recall: true,
  });

  const [error, setError] = useState(null);

  const loadData = useCallback(async (key) => {
    if (!key) return;

    // Reset all loading states
    setLoading({
      summary: true,
      alerts: true,
      patients: true,
      careGaps: true,
      coding: true,
      recall: true,
    });
    setError(null);

    // Carga individual por panel para skeleton loaders independientes
    const loadPanel = async (fetchFn, setter, panelKey) => {
      try {
        const data = await fetchFn(key);
        setter(data);
      } catch (err) {
        console.error(`Error loading ${panelKey}:`, err);
        setError((prev) => prev || `Failed to load ${panelKey}`);
      } finally {
        setLoading((prev) => ({ ...prev, [panelKey]: false }));
      }
    };

    // Lanzar todas las cargas en paralelo
    await Promise.allSettled([
      loadPanel(clinicalDataService.getSummary, setSummary, "summary"),
      loadPanel(clinicalDataService.getAlerts, setAlerts, "alerts"),
      loadPanel(clinicalDataService.getPatients, setPatients, "patients"),
      loadPanel(clinicalDataService.getCareGaps, setCareGaps, "careGaps"),
      loadPanel(clinicalDataService.getCoding, setCoding, "coding"),
      loadPanel(clinicalDataService.getRecall, setRecall, "recall"),
    ]);
  }, []);

  useEffect(() => {
    loadData(locationKey);
  }, [locationKey, loadData]);

  const refresh = useCallback(() => {
    loadData(locationKey);
  }, [locationKey, loadData]);

  return {
    summary,
    alerts,
    patients,
    careGaps,
    coding,
    recall,
    loading,
    error,
    refresh,
  };
}
